import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { SITE_INFO, NAVIGATION_ITEMS } from '../data/siteData'
import { useVirksomhedsoplysninger, useCMSCollection } from '../hooks/useCMS'

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(true)
  const [scrolled, setScrolled] = useState(false)
  const closeTimeoutRef = useRef(null)
  const location = useLocation()

  const { info } = useVirksomhedsoplysninger()
  const currentInfo = info || SITE_INFO

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setDropdownOpen(false)
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [location.pathname])

  // Track scroll with hysteresis buffer to completely prevent shaking/jumping
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY
          // Hysteresis buffer: scroll down past 45px to shrink, scroll back up above 15px to restore
          setScrolled((prev) => {
            if (!prev && y > 45) return true
            if (prev && y < 15) return false
            return prev
          })
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  // Desktop dropdown hover handlers with grace delay
  const handleDropdownMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setDropdownOpen(true)
  }

  const handleDropdownMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    // 200ms grace timeout lets the user naturally move cursor diagonally without dropdown closing
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false)
    }, 200)
  }

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-[#172e3b] text-[#FAF8F5]/85 text-xs py-2 px-4 border-b border-white/10 hidden sm:block">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <i className="fa-regular fa-location-dot text-[#7c875a]" aria-hidden="true" />
              <span>Vendersgade 37 kl., 7000 Fredericia</span>
            </span>
            <span className="hidden md:flex items-center gap-2 text-[#FAF8F5]/60">
              <span>RAB-godkendt Kropsterapeut &amp; Hypnoterapeut</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href={`tel:${currentInfo.phoneRaw}`}
              className="hover:text-white transition-colors flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium">{currentInfo.phone}</span>
            </a>
            <a
              href={`mailto:${currentInfo.email}`}
              className="hover:text-white transition-colors flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope text-[#7c875a]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium">{currentInfo.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-40 bg-[#FAF8F5]/95 backdrop-blur-md transition-all duration-300 ease-in-out ${
          scrolled ? 'shadow-sm border-b border-[#E5E0D8] py-2 sm:py-2.5' : 'border-b border-[#E5E0D8]/60 py-3.5 sm:py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Logo / Brandmark with smooth stable resize */}
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group focus:outline-none flex-shrink-0">
            <div className="flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-in-out">
              <img
                src={currentInfo.logos?.brandTransparent || SITE_INFO.logos.brandTransparent}
                alt="Bo Steendahl"
                className={`w-auto object-contain transition-all duration-300 ease-in-out group-hover:scale-[1.02] transform-gpu ${
                  scrolled ? 'h-11 sm:h-12 md:h-14' : 'h-14 sm:h-16 md:h-20'
                }`}
              />
            </div>
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2">
            {NAVIGATION_ITEMS.filter((item) => item.is_active_nav).map((item) => {
              if (item.children) {
                const isChildActive = item.children.some((c) => location.pathname === c.path)
                return (
                  <div
                    key={item.path}
                    className="relative"
                    onMouseEnter={handleDropdownMouseEnter}
                    onMouseLeave={handleDropdownMouseLeave}
                  >
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                          isActive || isChildActive
                            ? 'text-[#1b3a4b] bg-[#1b3a4b]/8 font-semibold'
                            : 'text-[#334155] hover:text-[#1b3a4b] hover:bg-black/5'
                        }`
                      }
                    >
                      <span>{item.title}</span>
                      <i
                        className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                          dropdownOpen ? 'rotate-180 text-[#7c875a]' : 'text-slate-400'
                        }`}
                        aria-hidden="true"
                      />
                    </NavLink>

                    {/* Dropdown Menu with invisible hover bridge and clear active color highlight */}
                    {dropdownOpen && (
                      <div
                        className="absolute top-full left-0 w-72 pt-2 -mt-1 z-50 before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 before:bg-transparent"
                        onMouseEnter={handleDropdownMouseEnter}
                        onMouseLeave={handleDropdownMouseLeave}
                      >
                        <div className="bg-white rounded-xl shadow-xl border border-[#E5E0D8] p-2 space-y-1">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              className={({ isActive }) =>
                                `flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-all ${
                                  isActive
                                    ? 'text-[#1b3a4b] bg-[#FAF8F5] font-semibold border-l-4 border-[#7c875a] pl-3 shadow-xs'
                                    : 'text-[#475569] hover:text-[#1b3a4b] hover:bg-[#FAF8F5] border-l-4 border-transparent'
                                }`
                              }
                            >
                              <span>{child.title}</span>
                              {location.pathname === child.path && (
                                <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a] flex-shrink-0" />
                              )}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                      isActive
                        ? 'text-[#1b3a4b] bg-[#1b3a4b]/8 font-semibold'
                        : 'text-[#334155] hover:text-[#1b3a4b] hover:bg-black/5'
                    }`
                  }
                >
                  {item.title}
                </NavLink>
              )
            })}
          </nav>

          {/* Desktop Right CTA */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-[#1b3a4b] hover:bg-[#132835] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all shadow-sm hover:shadow"
            >
              <span>Hør om et forløb</span>
              <i className="fa-solid fa-arrow-right text-xs text-[#7c875a]" aria-hidden="true" />
            </Link>
          </div>

          {/* Burger Menu Button (< 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-3">
            <a
              href={`tel:${SITE_INFO.phoneRaw}`}
              className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#1b3a4b]/5 text-[#1b3a4b]"
              aria-label="Ring til Bo Steendahl"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-[#1b3a4b] hover:bg-black/5 focus:outline-none"
              aria-label="Åbn navigationsmenu"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 min-[1200px]:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer Panel: Independently scrollable container without horizontal scroll */}
          <div className="fixed inset-y-0 right-0 max-w-sm w-full bg-[#FAF8F5] shadow-2xl flex flex-col h-full max-h-screen z-50 overflow-hidden">
            {/* Header inside drawer (fixed at top of drawer) */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-[#E5E0D8] flex items-center justify-between bg-[#FAF8F5]">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 focus:outline-none"
              >
                <img
                  src={currentInfo.logos?.brandTransparent || SITE_INFO.logos.brandTransparent}
                  alt="Bo Steendahl"
                  className="h-12 sm:h-14 w-auto object-contain"
                />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 -mr-2 text-[#1b3a4b] rounded-lg hover:bg-black/5 transition-colors focus:outline-none"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
              </button>
            </div>

            {/* Independently Scrollable Navigation Body */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 py-6 space-y-6">
              {/* Mobile Links */}
              <nav className="space-y-1">
                {NAVIGATION_ITEMS.filter((item) => item.is_active_nav).map((item) => {
                  if (item.children) {
                    const isParentActive = location.pathname === item.path
                    const isChildActive = item.children.some((c) => location.pathname === c.path)

                    return (
                      <div key={item.path} className="py-1">
                        {/* Parent item with navigation and toggle button */}
                        <div className="flex items-center justify-between rounded-lg">
                          <NavLink
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={({ isActive }) =>
                              `flex-1 px-3 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                                isActive || isChildActive
                                  ? 'text-[#1b3a4b] bg-[#1b3a4b]/8'
                                  : 'text-[#1e293b] hover:bg-black/5'
                              }`
                            }
                          >
                            {item.title}
                          </NavLink>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMobileSubmenuOpen(!mobileSubmenuOpen)
                            }}
                            className="p-2.5 text-[#1b3a4b] hover:bg-black/5 rounded-lg transition-transform focus:outline-none"
                            aria-label={`Udvid undermenu for ${item.title}`}
                            aria-expanded={mobileSubmenuOpen}
                          >
                            <i
                              className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                                mobileSubmenuOpen ? 'rotate-180 text-[#7c875a]' : 'text-slate-400'
                              }`}
                              aria-hidden="true"
                            />
                          </button>
                        </div>

                        {/* Collapsible Submenu with fully clickable subpages */}
                        {mobileSubmenuOpen && (
                          <div className="pl-3 mt-1.5 space-y-1 border-l-2 border-[#7c875a]/40 ml-3">
                            {item.children.map((child) => (
                              <NavLink
                                key={child.path}
                                to={child.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                  `flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-all ${
                                    isActive
                                      ? 'text-[#1b3a4b] font-semibold bg-[#1b3a4b]/10 border-l-3 border-[#7c875a] pl-3'
                                      : 'text-[#475569] hover:text-[#1b3a4b] hover:bg-black/5 border-l-3 border-transparent'
                                  }`
                                }
                              >
                                <span>{child.title}</span>
                                {location.pathname === child.path && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a] flex-shrink-0" />
                                )}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-3 py-2.5 text-base font-medium rounded-lg transition-colors ${
                          isActive
                            ? 'text-[#1b3a4b] bg-[#1b3a4b]/10 font-semibold border-l-4 border-[#7c875a] pl-3'
                            : 'text-[#1e293b] hover:bg-black/5 border-l-4 border-transparent'
                        }`
                      }
                    >
                      <span>{item.title}</span>
                      {location.pathname === item.path && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a] flex-shrink-0" />
                      )}
                    </NavLink>
                  )
                })}
              </nav>

              {/* Mobile CTA */}
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center justify-center gap-2 bg-[#1b3a4b] hover:bg-[#132835] text-white py-3.5 px-5 rounded-full text-base font-semibold shadow-md transition-colors"
                >
                  <span>Hør om et forløb</span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#7c875a]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Bottom info inside drawer (Fixed at bottom of drawer) */}
            <div className="flex-shrink-0 p-5 bg-[#F3EFEA] border-t border-[#E5E0D8] space-y-2 text-sm text-[#475569]">
              <div className="flex items-center gap-2.5">
                <i className="fa-solid fa-phone text-[#7c875a] text-xs" aria-hidden="true" />
                <a href={`tel:${currentInfo.phoneRaw}`} className="font-medium hover:text-[#1b3a4b] whitespace-nowrap">
                  {currentInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#7c875a] text-sm w-4 text-center" aria-hidden="true" />
                <a href={`mailto:${currentInfo.email}`} className="font-medium hover:text-[#1b3a4b] whitespace-nowrap">
                  {currentInfo.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-location-dot text-[#7c875a] text-sm w-4 text-center" aria-hidden="true" />
                <span>{currentInfo.fullAddress}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
